<template>
  <div>
    <template v-if="enrollments">
      <div>
        <div class="d-flex justify-center mb-4">
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
          <v-text-field
            v-model="filters.query"
            :aria-label="$bct('Search students by name or ID')"
            :placeholder="$bct('Search students by name or ID')"
            density="compact"
            prepend-inner-icon="search"
            variant="solo-filled"
            flat
            hide-details
          />
        </div>

        <v-row class="d-flex align-center mb-0">
          <v-col
            class="d-flex align-center"
            cols="12"
            md="6"
          >
            <v-btn
              v-if="store.state.pages.Interested.features.enable_mass_messaging"
              v-show="someChecked"
              @click="$refs.newMessageDialog.open()"
              :loading="processing"
              class="me-3"
              color="primary"
              data-cy="message-button"
              elevation="2"
            >
              {{ $bct('Message') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-card
          class="bb-0"
          border
          tile
        >
          <v-card-text class="fs-15">
            <v-row
              class="d-flex justify-space-between"
              dense
            >
              <v-col class="d-flex align-center">
                <template v-if="filterSummary">
                  <v-icon
                    icon="filter_alt"
                    start
                  />
                  <span class="me-1"> {{ $bct('Filtering by') }}: </span>
                  <span class="fw-600">
                    {{ filterSummary }}
                  </span>
                  <span>.&nbsp;</span>
                </template>

                <span>
                  <span v-if="filters.page == 1">
                    {{ $bct('Displaying first') }}
                    <strong>{{ enrollments?.length }}</strong> {{ $bct('of') }}
                    <strong>{{ totalItemCount }}</strong> {{ $bct('results') }}.
                  </span>
                  <span v-else>
                    {{ $bct('Displaying results') }}
                    <strong>
                      {{ pageStartCount }}-{{ pageStartCount + enrollments.length - 1 }}
                    </strong>
                    {{ $bct('out of') }} <strong>{{ totalItemCount }}</strong>
                    {{ $bct('results') }}.
                  </span>
                </span>
              </v-col>

              <v-col class="d-flex justify-end">
                <FilterMenu
                  ref="providerFilter"
                  :actions="false"
                  :disabled="someChecked"
                  :title="filteredProviderName || 'Location'"
                  classes="ms-3"
                  elevation="0"
                  width="400"
                  borderless
                  left
                  outlined
                >
                  <template #card>
                    <v-autocomplete
                      v-model="filters.provider_id"
                      @update:model-value="$refs.providerFilter.close()"
                      :item-title="getProviderNameWithAddress"
                      :items="providers"
                      :placeholder="$bct('Search by name or address')"
                      item-value="id"
                      variant="filled"
                      auto-select-first
                      clearable
                      left
                    />
                  </template>
                </FilterMenu>

                <FilterMenu
                  v-if="filterPrograms.length > 0"
                  ref="programFilter"
                  :actions="false"
                  :disabled="someChecked"
                  :title="filteredProgramName || terms.program"
                  classes="ms-3"
                  elevation="0"
                  width="400"
                  borderless
                  left
                  outlined
                >
                  <template #card>
                    <v-list
                      v-model:selected="filters.program_id"
                      density="compact"
                    >
                      <v-list-item
                        v-for="program in filterPrograms"
                        :key="program.id"
                        :title="$bct(program.name)"
                        :value="program.id"
                        color="primary"
                        data-cy="availability-checkmark"
                      >
                        <template #prepend="{ isSelected, select }">
                          <v-checkbox-btn
                            @update:model-value="select"
                            :model-value="isSelected"
                            density="compact"
                            tabindex="-1"
                          >
                            <template #input="{ inputNode, icon }">
                              <v-icon
                                :icon="icon"
                                size="x-small"
                              />

                              <component :is="inputNode" />
                            </template>
                          </v-checkbox-btn>
                        </template>
                      </v-list-item>
                    </v-list>
                  </template>
                </FilterMenu>

                <ActionMenu
                  @click:action:export="download('csv')"
                  :items="[{ title: 'Export to CSV for Excel', event: 'export' }]"
                  button-icon="more_vert"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="mb-2"
          border
          tile
        >
          <DataTableServer
            v-model="checkedEnrollments"
            @click:row="displayEnrollment"
            @update:page="handlePageChange"
            @update:sort-by="handleSortChange"
            :headers="tableHeaders"
            :hide-default-footer="!totalItemCount"
            :items="enrollments"
            :items-length="totalItemCount"
            :items-per-page="itemsPerPage"
            :loading="processing"
            :page="filters.page"
            :row-props="getRowProps"
            item-key="id"
            fixed-header
            return-object
            show-select
          >
            <template #[`item.name`]="{ item: enrollment }">
              <v-badge
                v-if="getEnrolledFormMissing(enrollment)"
                data-testid="enrollment-badge-dot"
                dot
                inline
              />
              {{ enrollment.name }}

              <v-badge
                v-if="!enrollment.seen_by_provider_at"
                :content="$bct('NEW')"
                :label="$bct('New enrollment')"
                color="primary"
                text-color="white"
                inline
              />
            </template>

            <template #[`item.dob`]="{ item: enrollment }">
              {{ enrollment.meta.child.dob }}
            </template>

            <template #[`item.address`]="{ item: enrollment }">
              {{ enrollment.meta.group.home_address }} {{ enrollment.meta.group.home_city }}
            </template>

            <template #[`item.provider`]="{ item: enrollment }">
              {{ enrollment.meta.provider.name }}
            </template>

            <template #[`item.program`]="{ item: enrollment }">
              {{ enrollment.meta.program?.name }}
            </template>
          </DataTableServer>
        </v-card>
      </div>
    </template>

    <template v-else>
      <v-progress-linear
        class="mt-6"
        indeterminate
      />
    </template>
  </div>

  <EnrollmentDialog
    @change="load"
    @close="load"
    ref="enrollDialog"
    :subsidy-programs="subsidyPrograms"
  />

  <ResourceDialog
    @save="sendMassMessage"
    ref="newMessageDialog"
    :processing="processing"
    :save-button-disabled="!massMessage.subject || !massMessage.body"
    save-button-text="Send"
    title="New message"
  >
    <template #form>
      <v-row>
        <LabeledTextfield
          v-model="massMessage.subject"
          message="Subject"
        />
        <LabeledTextarea
          v-model="massMessage.body"
          message="Body"
        />
        <v-col class="fs-16">
          <p>
            {{ $bct('Message will be sent to') }}: <strong>{{ recipientCount }}</strong>
            {{ $bct('recipients') }}
          </p>
        </v-col>
      </v-row>
    </template>
  </ResourceDialog>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import ActionMenu from '@/shared/components/ActionMenu.vue';
import { createDateTimeFileName } from '@/shared/services/date-time-file-name';
import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import { ENROLLMENT_STATUSES } from '@/shared/assets/constants';
import EnrollmentDialog from '@/manager/components/enrollment/EnrollmentDialog.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useTerms from '@/shared/composables/useTerms';

const $bct = inject('translator');

const { api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const { updateQuery } = useRouterHelper();
const store = useStore();
const { terms } = useTerms();

const props = defineProps({
  subsidyProgramId: {
    type: String,
    default: null,
  },
  subsidyPrograms: {
    type: Array,
    default: null,
  },
});

const checkedEnrollments = ref([]);
const enrollDialog = ref(null);
const enrollments = ref(null);
const filterPrograms = ref([]);
const filters = ref({
  program_id: route.query.programId,
  provider_id: route.query.providerId,
  sort_dir: null,
  sort_field: null,
  page: 1,
  query: null,
});
const massMessage = ref({ subject: null, body: null });
const newMessageDialog = ref(null);
const itemsPerPage = ref(null);
const pageStartCount = ref(0);
const processing = ref(false);
const providers = ref([]);
const queryCount = ref(0);
const subsidyProgram = ref(null);
const totalItemCount = ref(0);

const tableHeaders = computed(() => {
  return [
    { title: 'Child', key: 'name' },
    { title: 'DOB', key: 'dob' },
    { title: 'Home', key: 'address' },
    { title: 'Location', key: 'provider' },
    { title: terms.value.program, key: 'program' },
  ];
});

const filterSummary = computed(() => {
  const appliedFilters = [];
  if (filteredProviderName.value) appliedFilters.push(filteredProviderName.value);
  if (filteredProgramName.value) appliedFilters.push(filteredProgramName.value);

  return appliedFilters.length > 0 ? appliedFilters.join(', ') : null;
});

const filteredProgramName = computed(() => {
  if (filters.value.program_id && filters.value.provider_id) {
    return filterPrograms.value.find((program) => program.id === filters.value.program_id)?.name;
  }

  return null;
});

const filteredProviderName = computed(() => {
  if (filters.value.provider_id && providers.value.length > 0) {
    return providers.value.find((provider) => provider.id === filters.value.provider_id)?.name;
  }

  return null;
});

const someChecked = computed(() => {
  return checkedEnrollments.value.length > 0;
});

const recipientCount = computed(() => {
  return checkedEnrollments.value.length;
});

watch(
  () => filters.value.provider_id,
  async (newProviderId, oldProviderId) => {
    if (newProviderId === oldProviderId) return;

    await loadPrograms(newProviderId);
    if (
      filters.value.program_id &&
      !filterPrograms.value.find((program) => program.id === filters.value.program_id)
    ) {
      filters.value.program_id = null;
      return; // The change above will trigger watch that calls load
    }

    void updateFiltersAndReload();
  },
  { immediate: true },
);

watch([() => filters.value.program_id, () => filters.value.query], () => {
  void updateFiltersAndReload();
});

watch(
  () => props.subsidyProgramId,
  () => {
    checkedEnrollments.value = [];

    if (filters.value) {
      filters.value.program_id = null;
      filters.value.sort_dir = null;
      filters.value.sort_field = null;
      filters.value.page = 1;
    }
    void load();
  },
);

async function displayEnrollment(_event, { item: enrollment }) {
  enrollDialog.value.open(enrollment);
  if (enrollment.seen_by_provider_at) return;

  const params = {
    seen_by_provider: true,
  };
  await api.manager.enrollment.update(enrollment.id, params);
  await load();
}

function download(format) {
  const params = {
    status: ENROLLMENT_STATUSES.ENROLLED,
  };

  if (filters.value.provider_id) params.provider_id = filters.value.provider_id;
  if (filters.value.program_id) params.program_id = filters.value.program_id;
  if (props.subsidyProgramId) params.subsidy_program_id = props.subsidyProgramId;

  const fileName = createDateTimeFileName(
    store.state.pages.Students.title || 'Students',
    store.state.profile.default_locale,
  );

  const url = api.manager.enrollment.downloadUrl(params, fileName, format);
  window.open(url);
}

function getEnrolledFormMissing(enrollment) {
  if (!subsidyProgram.value?.enrolled_form_schema_id) return false;
  return !enrollment.enrolled_form_id;
}

function getProviderNameWithAddress(provider) {
  return [provider.name, provider.address1].filter(Boolean).join(' - ');
}

function getRowProps({ item: enrollment }) {
  const baseProps = { 'data-testid': 'enrollment-row' };
  if (enrollment.seen_by_provider_at) return baseProps;
  return { ...baseProps, class: 'fw-900' };
}

async function load() {
  if (subsidyProgram.value?.id !== props.subsidyProgramId) {
    subsidyProgram.value = props.subsidyPrograms?.find((sp) => sp.id === props.subsidyProgramId);

    await loadProviders();

    if (
      filters.value.provider_id &&
      !providers.value.find((provider) => provider.id === filters.value.provider_id)
    ) {
      filters.value.provider_id = null;
      return; // The change above will trigger watch that calls load
    }
  }

  const filtersAndParams = {
    subsidy_program_id: props.subsidyProgramId,
    ...filters.value,
  };
  queryCount.value += 1;
  const currentQueryCount = queryCount.value;

  filtersAndParams.status = ENROLLMENT_STATUSES.ENROLLED;

  api.manager.enrollment.index(
    filtersAndParams,
    (response) => {
      if (queryCount.value === currentQueryCount) {
        enrollments.value = response.data;

        itemsPerPage.value = parseInt(response.headers['x-page-size'] || 0, 10);
        totalItemCount.value = parseInt(response.headers['x-total-count'] || 0, 10);
        pageStartCount.value = itemsPerPage.value * (filters.value.page - 1) + 1;

        checkedEnrollments.value = [];
        processing.value = false;
      }
    },
    (err) => {
      eventBus.error(err);
    },
  );
}

async function loadPrograms(providerId) {
  if (!providerId) {
    filterPrograms.value = [];
    return;
  }

  const programFilters = {
    subsidy_program_id: props.subsidyProgramId,
  };
  const { data } = await api.program.index(providerId, programFilters);
  filterPrograms.value = data;
}

async function loadProviders() {
  const providerFilters = {
    subsidy_program_id: props.subsidyProgramId,
  };
  const { data } = await api.manager.provider.index(providerFilters);
  providers.value = data;
}

async function sendMassMessage() {
  processing.value = true;
  massMessage.value.enrollment_ids = checkedEnrollments.value.map((enrollment) => enrollment.id);
  api.manager.mass_message.create(
    massMessage.value,
    async () => {
      newMessageDialog.value.close();
      processing.value = false;
      eventBus.chime('Message sent');
      await load();
    },
    (err) => {
      processing.value = false;
      const errorMessage = err.response?.data?.errors?.join(', ') || 'An error occurred';
      eventBus.chime(errorMessage);
    },
  );
}

function handlePageChange(newPageValue) {
  filters.value.page = newPageValue;
  void updateFiltersAndReload(true);
}

function handleSortChange(sorts) {
  // Note: we currently only support a single sort/dir
  // so we only access the first item in passed array
  if (sorts.length === 0) {
    filters.value.sort_field = null;
    filters.value.sort_dir = null;
  } else {
    filters.value.sort_field = sorts[0].key;
    filters.value.sort_dir = sorts[0].order;
  }
  processing.value = true;
  void updateFiltersAndReload();
}

async function updateFiltersAndReload(paged = false) {
  if (!paged) filters.value.page = 1;
  await updateQuery(filters.value);
  void load();
}

onMounted(load);
</script>
